import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import Script from 'dangerous-html/react'
import { Helmet } from 'react-helmet'

import Navigation from '../components/navigation'
import Footer from '../components/footer'
import FrameCalculator from '../components/FrameCalculator'
import { usePageView, useViewTracking } from '../hooks/useAnalytics'
import './home.css'

const Home = (props) => {
  const { t } = useTranslation('home')

  // Track page view
  usePageView('Home');

  // Section visibility tracking
  const heroRef = useViewTracking('Hero Section');
  const philosophyRef = useViewTracking('Philosophy Section');
  const materialsRef = useViewTracking('Materials Section');
  const galleryRef = useViewTracking('Gallery Section');
  const ctaRef = useViewTracking('CTA Section');
  const calculatorRef = useViewTracking('Calculator Section');

  const scrollToCalculator = () => {
    const calculatorSection = document.getElementById('calculatorSection')
    if (!calculatorSection) return

    const header = document.querySelector('.navigation')
    const headerHeight = header ? header.offsetHeight : 80
    const rect = calculatorSection.getBoundingClientRect()
    const absoluteTop = rect.top + window.pageYOffset
    const visibleHeight = window.innerHeight - headerHeight
    const scrollPosition = absoluteTop - headerHeight - (visibleHeight / 2) + (rect.height / 2) + 110

    window.scrollTo({
      top: Math.max(0, scrollPosition),
      behavior: 'smooth'
    })
  }

  useEffect(() => {
    if (window.location.hash === '#calculatorSection') {
      window.setTimeout(scrollToCalculator, 100)
    }
  }, [])

  return (
    <div className="home-container1">
      <Helmet>
        <title>ORRAM Frames</title>
        <meta name="keywords" content={t('meta.keywords')} />
        <meta property="og:title" content="ORRAM Frames - Custom Aluminum Picture Frames" />
      </Helmet>
      <Navigation></Navigation>
      <div className="home-container2">
        <div className="home-container3">
          <Script
            html={`<style>
* {
  transition-property: transform, opacity, color, background-color,
      border-color, box-shadow;
}
@media (prefers-reduced-motion: reduce) {
* {
  animation-duration: 0.01ms !important;
  animation-iteration-count: 1 !important;
  transition-duration: 0.01ms !important;
}
}
</style>`}
          ></Script>
        </div>
      </div>
      <div className="home-container4">
        <div className="home-container5">
          <Script
            html={`<script defer data-name="ORRAM Frames Homepage">
(function(){
  // Hero calculator button scroll - centers calculator in viewport
  const heroCalculatorBtn = document.getElementById("heroCalculatorBtn")
  const calculatorSection = document.getElementById("calculatorSection")

  if (heroCalculatorBtn && calculatorSection) {
    heroCalculatorBtn.addEventListener("click", () => {
      // Get header height for offset
      const header = document.querySelector('.navigation')
      const headerHeight = header ? header.offsetHeight : 80

      // Get calculator position and dimensions
      const rect = calculatorSection.getBoundingClientRect()
      const absoluteTop = rect.top + window.pageYOffset

      // Center calculator in visible viewport (accounting for header)
      // Add 110px offset to scroll a bit more down
      const visibleHeight = window.innerHeight - headerHeight
      const scrollPosition = absoluteTop - headerHeight - (visibleHeight / 2) + (rect.height / 2) + 110

      window.scrollTo({
        top: Math.max(0, scrollPosition),
        behavior: 'smooth'
      })
    })
  }

  // Parallax effect for hero
  window.addEventListener("scroll", () => {
    const scrolled = window.pageYOffset
    const heroVideo = document.querySelector(".hero-video")
    if (heroVideo) {
      heroVideo.style.transform = \`translateY(\${scrolled * 0.5}px)\`
    }
  })

  // Gallery hover effects
  const galleryItems = document.querySelectorAll(".gallery-item")
  galleryItems.forEach((item) => {
    item.addEventListener("mouseenter", () => {
      galleryItems.forEach((otherItem) => {
        if (otherItem !== item) {
          otherItem.style.opacity = "0.7"
          otherItem.style.transform = "scale(0.98)"
        }
      })
    })

    item.addEventListener("mouseleave", () => {
      galleryItems.forEach((otherItem) => {
        otherItem.style.opacity = "1"
        otherItem.style.transform = "scale(1)"
      })
    })
  })

  // Material cards stagger animation on scroll
  const observerOptions = {
    threshold: 0.1,
    rootMargin: "0px 0px -100px 0px",
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry, index) => {
      if (entry.isIntersecting) {
        setTimeout(() => {
          entry.target.style.transform = "translateY(0)"
          entry.target.style.opacity = "1"
        }, index * 100)
      }
    })
  }, observerOptions)

  // Observe material cards
  const materialCards = document.querySelectorAll(".material-card")
  materialCards.forEach((card) => {
    card.style.transform = "translateY(30px)"
    card.style.opacity = "0"
    card.style.transition = "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)"
    observer.observe(card)
  })
})()
</script>`}
          ></Script>
        </div>
      </div>
      <section ref={heroRef} className="hero-section">
        <div className="hero-background">
          <video
            src="/hero-video.mp4"
            loop="true"
            muted="true"
            autoPlay="true"
            playsInline="true"
            className="hero-video"
          ></video>
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              <span className="hero-title-orram">ORRAM</span>
              <br></br>
              <span className="home-hero-title-accent">Frames</span>
            </h1>
            <p className="hero-subtitle">
              {t('hero.tagline')}
              <br></br>
              {t('hero.description')}
            </p>
            <div className="hero-actions">
              <button
                id="heroCalculatorBtn"
                className="hero-calculator-btn btn btn-outline"
              >
                <svg
                  width="24"
                  xmlns="http://www.w3.org/2000/svg"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <g
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect x="4" y="2" rx="2" width="16" height="20"></rect>
                    <path d="M8 6h8m0 8v4m0-8h.01M12 10h.01M8 10h.01M12 14h.01M8 14h.01M12 18h.01M8 18h.01"></path>
                  </g>
                </svg>
                <span>{t('hero.ctaButton')}</span>
              </button>
            </div>
          </div>
        </div>
      </section>
      <section ref={philosophyRef} className="philosophy-section">
        <div className="section-container">
          <div className="philosophy-content">
            <div className="philosophy-text">
              <h2 className="section-title">{t('philosophy.title')}</h2>
              <p className="section-content">
                {t('philosophy.description')}
              </p>
              <div className="philosophy-stats">
                <div className="stat-item">
                  <span className="stat-number">{t('philosophy.stats.tradition.value')}</span>
                  <span className="stat-label">{t('philosophy.stats.tradition.label')}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{t('philosophy.stats.masterpieces.value')}</span>
                  <span className="stat-label">{t('philosophy.stats.masterpieces.label')}</span>
                </div>
              </div>
            </div>
            <div className="philosophy-visual">
              <div className="golden-spiral">
                <img
                  alt="Professional paint booth where ORRAM frames are finished with precision coating."
                  src="/images/paint-booth.jpeg"
                  className="craftsmanship-image"
                />
                <div className="spiral-overlay"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section ref={materialsRef} className="materials-section">
        <div className="section-container">
          <div className="materials-header">
            <h2 className="section-title">{t('materials.title')}</h2>
            <p className="section-subtitle">
              {t('materials.subtitle')}
            </p>
          </div>
          <div className="materials-grid">
            <div className="material-card">
              <div className="material-image">
                <img
                  alt="CNC machine cutting frame components with precision in ORRAM workshop."
                  src="/images/cnc-machine.jpeg"
                />
                <div className="material-overlay">
                  <svg
                    width="24"
                    xmlns="http://www.w3.org/2000/svg"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <g
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="m15.477 12.89l1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526"></path>
                      <circle r="6" cx="12" cy="8"></circle>
                    </g>
                  </svg>
                </div>
              </div>
              <div className="material-content">
                <h3 className="material-title">{t('materials.precision.title')}</h3>
                <p className="material-description">
                  {t('materials.precision.description')}
                </p>
              </div>
            </div>
            <div className="material-card">
              <div className="material-image">
                <img
                  alt="Golden ratio applied to ORRAM frame design."
                  src="/images/philosophy-golden-ratio.jpg"
                />
                <div className="material-overlay">
                  <svg
                    width="24"
                    xmlns="http://www.w3.org/2000/svg"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    ></path>
                  </svg>
                </div>
              </div>
              <div className="material-content">
                <h3 className="material-title">{t('materials.goldenRatio.title')}</h3>
                <p className="material-description">
                  {t('materials.goldenRatio.description')}
                </p>
              </div>
            </div>
            <div className="material-card">
              <div className="material-image">
                <img
                  alt="ORRAM frame materials — lacquered and raw finishes."
                  src="/images/material-of-time.jpg"
                />
                <div className="material-overlay">
                  <svg
                    width="24"
                    xmlns="http://www.w3.org/2000/svg"
                    height="24"
                    viewBox="0 0 24 24"
                  >
                    <g
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M12 22a1 1 0 0 1 0-20a10 9 0 0 1 10 9a5 5 0 0 1-5 5h-2.25a1.75 1.75 0 0 0-1.4 2.8l.3.4a1.75 1.75 0 0 1-1.4 2.8z"></path>
                      <circle
                        r=".5"
                        cx="13.5"
                        cy="6.5"
                        fill="currentColor"
                      ></circle>
                      <circle
                        r=".5"
                        cx="17.5"
                        cy="10.5"
                        fill="currentColor"
                      ></circle>
                      <circle
                        r=".5"
                        cx="6.5"
                        cy="12.5"
                        fill="currentColor"
                      ></circle>
                      <circle
                        r=".5"
                        cx="8.5"
                        cy="7.5"
                        fill="currentColor"
                      ></circle>
                    </g>
                  </svg>
                </div>
              </div>
              <div className="material-content">
                <h3 className="material-title">{t('materials.timelessMaterials.title')}</h3>
                <p className="material-description">
                  {t('materials.timelessMaterials.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section ref={galleryRef} className="gallery-section">
        <div className="section-container">
          <div className="gallery-header">
            <h2 className="section-title">{t('gallery.title')}</h2>
            <p className="section-subtitle">
              {t('gallery.subtitle')}
            </p>
          </div>
          <div className="gallery-grid">
            <div className="gallery-item gallery-large">
              <img
                alt="Happy client presenting a large custom ORRAM frame with black and white photography."
                src="/images/client-frame.jpeg"
              />
              <div className="gallery-overlay">
                <span className="gallery-title">Contemporary Collection</span>
              </div>
            </div>
            <div className="gallery-item">
              <img
                alt="ORRAM frame displayed in bedroom setting."
                src="/images/gallery-bedroom-elegance.jpg"
              />
              <div className="gallery-overlay">
                <span className="gallery-title">Bedroom Elegance</span>
              </div>
            </div>
            <div className="gallery-item">
              <img
                alt="ORRAM frame with gold accent details."
                src="/images/gallery-gold-accent.jpg"
              />
              <div className="gallery-overlay">
                <span className="gallery-title">Gold Accent Details</span>
              </div>
            </div>
            <div className="gallery-item">
              <img
                alt="Collection of finished ORRAM frames."
                src="/images/gallery-classic-precision.jpg"
              />
              <div className="gallery-overlay">
                <span className="gallery-title">Classic Precision</span>
              </div>
            </div>
            <div className="gallery-item gallery-tall">
              <img
                alt="ORRAM frame with luxury textures."
                src="/images/gallery-luxury-textures.jpg"
              />
              <div className="gallery-overlay">
                <span className="gallery-title">Luxury Textures</span>
              </div>
            </div>
            <div className="gallery-item">
              <img
                alt="ORRAM frame with sophisticated lines."
                src="/images/gallery-sophisticated-lines.jpg"
              />
              <div className="gallery-overlay">
                <span className="gallery-title">Sophisticated Lines</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section ref={ctaRef} className="cta-section">
        <div className="cta-background">
          <video
            src="/cta-video.mp4"
            loop="true"
            muted="true"
            autoPlay="true"
            playsInline="true"
            className="cta-video"
          ></video>
          <div className="cta-overlay"></div>
        </div>
        <div className="cta-content">
          <h2 className="section-title">{t('cta.title')}</h2>
          <p className="section-subtitle">
            {t('cta.description')}
            <br></br>
            {t('cta.subtitle')}
          </p>
          <div className="cta-actions">
            <button className="btn-lg btn btn-primary" onClick={scrollToCalculator}>
              {t('cta.orderButton')}
            </button>
          </div>
          <div className="trust-indicators">
            <div className="trust-item">
              <svg
                width="24"
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 0 24 24"
              >
                <path
                  d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
              </svg>
              <span>{t('cta.trust.guarantee')}</span>
            </div>
            <div className="trust-item">
              <svg
                width="24"
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 0 24 24"
              >
                <path
                  d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.12 2.12 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.12 2.12 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.12 2.12 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.12 2.12 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.12 2.12 0 0 0 1.597-1.16z"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
              </svg>
              <span>{t('cta.trust.craftsmen')}</span>
            </div>
            <div className="trust-item">
              <svg
                width="24"
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 0 24 24"
              >
                <path
                  d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Zm-6.8-2.8l2-2m-5-1l2-2m-5-1l2-2m7 11l2-2"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                ></path>
              </svg>
              <span>{t('cta.trust.precision')}</span>
            </div>
          </div>
        </div>
      </section>
      <section ref={calculatorRef} id="calculatorSection" className="calculator-section py-16">
        {/* Header stays in limited container */}
        <div className="section-container">
          <div className="calculator-header text-center mb-12">
            <h2 className="section-title">{t('calculator.title')}</h2>
            <p className="section-subtitle">
              {t('calculator.description')}
            </p>
          </div>
        </div>
        {/* Calculator takes full width - outside section-container */}
        <FrameCalculator />
      </section>
      <Footer></Footer>
    </div>
  )
}

export default Home
